import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import contabilidadReportService from '../../../services/contabilidadReportService';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import Toast from '../../../components/Common/Toast';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const formatMoney = (amount, currency = 'PEN') => {
  const value = parseFloat(amount) || 0;
  const symbol = currency === 'USD' ? 'US$' : 'S/';
  return `${symbol} ${value.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const classificationBadge = (key, label) => {
  const styles = {
    con_almacen: 'bg-teal-100 text-teal-800',
    sin_almacen: 'bg-amber-100 text-amber-800',
    mixto: 'bg-indigo-100 text-indigo-800',
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[key] || 'bg-gray-100 text-gray-800'}`}>
      {label}
    </span>
  );
};

const DetailModal = ({ detail, loading, onClose }) => {
  const doc = detail?.document;
  const currency = doc?.currency || 'PEN';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {loading || !doc ? 'Cargando detalle…' : `Detalle — ${doc.code}`}
            </h2>
            {doc && (
              <p className="text-sm text-gray-500 mt-0.5">
                {doc.document_type_label} · {doc.serie_numero} · {doc.supplier_name}
                {doc.supplier_document ? ` · ${doc.supplier_document}` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {loading || !doc ? (
          <div className="p-10 text-center text-gray-500">Cargando…</div>
        ) : (
          <div className="p-5 space-y-6">
            {/* 1. Referencias (guías) */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Referencias (guías de ingreso)
              </h3>
              {detail.referencias.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 rounded-md p-3">
                  Este documento no tiene guías de ingreso asociadas.
                </p>
              ) : (
                <div className="space-y-3">
                  {detail.referencias.map((ref) => (
                    <div key={ref.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-3 py-2">
                        <div className="text-sm">
                          <span className="font-mono font-medium text-teal-700">{ref.code}</span>
                          {ref.warehouse_name && (
                            <span className="text-gray-500"> · {ref.warehouse_name}</span>
                          )}
                          {ref.entry_date && (
                            <span className="text-gray-400"> · {formatDateLocaleLima(ref.entry_date)}</span>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-gray-800">
                          {formatMoney(ref.total_amount, ref.currency)}
                        </span>
                      </div>
                      {(ref.insumos || []).length > 0 ? (
                        <table className="min-w-full text-sm">
                          <thead className="bg-white border-b border-gray-100">
                            <tr>
                              <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-400 uppercase">Producto</th>
                              <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-400 uppercase">Cant.</th>
                              <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-400 uppercase">P. Unit</th>
                              <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-400 uppercase">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {ref.insumos.map((insumo) => (
                              <tr key={insumo.id}>
                                <td className="px-3 py-1.5 text-gray-700">
                                  {insumo.product_name}
                                  {insumo.sku && <span className="text-gray-400"> · {insumo.sku}</span>}
                                </td>
                                <td className="px-3 py-1.5 text-right text-gray-600 whitespace-nowrap">
                                  {insumo.quantity} {insumo.unit_of_measure || ''}
                                </td>
                                <td className="px-3 py-1.5 text-right text-gray-600 whitespace-nowrap">
                                  {formatMoney(insumo.unit_price, ref.currency)}
                                </td>
                                <td className="px-3 py-1.5 text-right font-medium text-gray-800 whitespace-nowrap">
                                  {formatMoney(insumo.total, ref.currency)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="px-3 py-2 text-sm text-gray-400">Sin productos en esta guía.</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* 2. Insumos (consolidado) */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Insumos (consolidado)
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Resumen: cada total con su referencia al lado */}
                <div className="bg-gray-50 px-4 py-3 space-y-2 text-sm">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <div className="min-w-0 flex-1">
                      <span className="text-gray-600">Total guía</span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="font-mono text-teal-700 font-medium">
                        {detail.referencias.length > 0
                          ? detail.referencias.map((r) => r.code).join(', ')
                          : '—'}
                      </span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="font-medium text-gray-800">{doc.supplier_name || '—'}</span>
                      <span className="text-xs text-gray-400 ml-1.5">(referencia)</span>
                    </div>
                    <span className="font-semibold text-gray-900 whitespace-nowrap">
                      {formatMoney(detail.resumen?.guias_total_referencia ?? 0, currency)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <div className="min-w-0 flex-1">
                      <span className="text-gray-600">Total insumos documentados</span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="font-medium text-indigo-700">{doc.document_type_label || '—'}</span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="font-medium text-gray-800">{doc.supplier_name || '—'}</span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="font-mono text-gray-700">{doc.serie_numero || '—'}</span>
                      <span className="text-xs text-gray-400 ml-1.5">(referencia)</span>
                    </div>
                    <span className="font-semibold text-teal-800 whitespace-nowrap">
                      {formatMoney(detail.resumen?.insumos_documentados_con_guia ?? 0, currency)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-gray-200 pt-2">
                    <span className="font-medium text-gray-700">Total documentado de la guía</span>
                    <span className="font-bold text-gray-900 whitespace-nowrap">
                      {formatMoney(detail.resumen?.documentado_de_guia ?? 0, currency)}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. No entraron a almacén */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                No entraron a almacén
              </h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {(detail.documentada.items_no_almacen || []).length === 0 ? (
                  <p className="px-4 py-3 text-sm text-gray-500">
                    No hay ítems sin ingreso a almacén en este documento.
                  </p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase">Ítem</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">Cant.</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">P. Unit</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detail.documentada.items_no_almacen.map((item) => (
                        <tr key={item.id}>
                          <td className="px-3 py-2 text-gray-700">
                            {item.name}
                            {item.sku && <span className="text-gray-400"> · {item.sku}</span>}
                            {item.item_type === 'service' && (
                              <span className="ml-1 text-xs text-indigo-600">(servicio)</span>
                            )}
                            {item.is_direct_consumption && (
                              <span className="ml-1 text-xs text-amber-600">(consumo directo)</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">
                            {item.quantity} {item.unit_of_measure || ''}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">
                            {formatMoney(item.unit_price, currency)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-gray-800 whitespace-nowrap">
                            {formatMoney(item.subtotal, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-amber-50 border-t border-gray-200">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-right text-sm font-semibold text-gray-700">
                          Total no almacén
                        </td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-gray-900 whitespace-nowrap">
                          {formatMoney(detail.resumen?.no_almacen ?? 0, currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </section>

            {/* 4. Totales documentados (fiscales) */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                Documentado (totales)
              </h3>
              <div className="border border-gray-200 rounded-lg bg-gray-50 p-4">
                <dl className="max-w-sm ml-auto space-y-1.5 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">Total insumos</dt>
                    <dd className="font-medium text-gray-800">
                      {formatMoney(detail.documentada.base_imponible, currency)}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500">IGV ({detail.documentada.igv_rate ?? 18}%)</dt>
                    <dd className="font-medium text-gray-800">
                      {formatMoney(detail.documentada.igv_amount, currency)}
                    </dd>
                  </div>
                  {Number(detail.documentada.no_gravados) > 0 && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-gray-500">No gravados</dt>
                      <dd className="font-medium text-gray-800">
                        {formatMoney(detail.documentada.no_gravados, currency)}
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4 border-t border-gray-200 pt-1.5">
                    <dt className="font-semibold text-gray-700">Precio total</dt>
                    <dd className="font-bold text-gray-900">
                      {formatMoney(detail.documentada.total_amount, currency)}
                    </dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const ComprasDiferenciadas = () => {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const years = Array.from({ length: 8 }, (_, i) => now.getFullYear() - i);

  const [rows, setRows] = useState([]);
  const [totalsByClassification, setTotalsByClassification] = useState([]);
  const [totalsByCurrency, setTotalsByCurrency] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const openDetail = async (documentId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const response = await contabilidadReportService.getComprasDiferenciadasDetalle(documentId);
      if (response.success) {
        setDetail(response.data);
      } else {
        setDetail(null);
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el detalle',
          type: 'error',
        });
      }
    } catch (error) {
      setDetail(null);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el detalle',
        type: 'error',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetail(null);

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const response = await contabilidadReportService.getComprasDiferenciadasExport({
        year: selectedYear,
        month: selectedMonth,
      });
      if (!response.success) {
        setToast({
          isOpen: true,
          message: response.message || 'Error al preparar la exportación',
          type: 'error',
        });
        return;
      }

      const exportRows = response.data?.rows || [];
      if (!exportRows.length) {
        setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Detalle compras');

      const title = `Compras diferenciadas — ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`;
      sheet.mergeCells('A1:V1');
      sheet.getCell('A1').value = title;
      sheet.getCell('A1').font = { bold: true, size: 14 };

      const headers = [
        'N°', 'Código', 'Tipo', 'Serie/Número', 'Proveedor', 'Doc. proveedor',
        'Fecha', 'Clasificación', 'Sección', 'Guía', 'Almacén', 'Concepto / Producto',
        'SKU', 'Cantidad', 'Unidad', 'P. Unit', 'Monto', 'Base imponible', 'IGV',
        'No gravados', 'Total documento', 'Moneda',
      ];
      const headerRow = sheet.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' },
      };

      exportRows.forEach((row) => {
        sheet.addRow([
          row.n,
          row.codigo,
          row.tipo,
          row.serie_numero,
          row.proveedor,
          row.doc_proveedor || '',
          row.fecha || '',
          row.clasificacion,
          row.seccion,
          row.guia || '',
          row.almacen || '',
          row.concepto || '',
          row.sku || '',
          row.cantidad ?? '',
          row.unidad || '',
          row.p_unit ?? '',
          row.monto ?? '',
          row.base_imponible ?? '',
          row.igv ?? '',
          row.no_gravados ?? '',
          row.total_documento ?? '',
          row.moneda || '',
        ]);
      });

      // AutoFilter sobre el rango de datos (fila de encabezados = 2)
      sheet.autoFilter = {
        from: { row: 2, column: 1 },
        to: { row: 2 + exportRows.length, column: headers.length },
      };

      // Congelar título + encabezados
      sheet.views = [{ state: 'frozen', ySplit: 2 }];

      const widths = [6, 12, 14, 16, 28, 18, 12, 22, 22, 12, 18, 40, 16, 10, 10, 12, 12, 14, 12, 12, 14, 10];
      widths.forEach((w, i) => {
        sheet.getColumn(i + 1).width = w;
      });

      // Formato numérico en columnas de montos
      [14, 16, 17, 18, 19, 20, 21].forEach((col) => {
        sheet.getColumn(col).numFmt = '#,##0.00';
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `compras-diferenciadas-detalle-${selectedYear}-${String(selectedMonth).padStart(2, '0')}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: `Excel exportado (${exportRows.length} filas)`,
        type: 'success',
      });
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al exportar Excel',
        type: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await contabilidadReportService.getComprasDiferenciadas({
        year: selectedYear,
        month: selectedMonth,
      });
      if (response.success) {
        setRows(response.data?.rows || []);
        setTotalsByClassification(response.data?.totals_by_classification || []);
        setTotalsByCurrency(response.data?.totals_by_currency || []);
        setHasQueried(true);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el reporte',
          type: 'error',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el reporte',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <nav className="text-sm text-gray-600 mb-4">
          <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Contabilidad</span>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Compras diferenciadas</span>
        </nav>
        <h1 className="text-2xl font-bold text-gray-800">Compras diferenciadas</h1>
        <p className="text-sm text-gray-600 mt-1">
          Documentos del mes clasificados por ingreso a almacén vs gasto/servicio
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[160px]"
            >
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>{name}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={loadReport}
            disabled={loading}
            className="px-5 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-60 font-medium"
          >
            {loading ? 'Consultando…' : 'Consultar'}
          </button>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={exporting || loading}
            className="px-5 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-60 font-medium"
          >
            {exporting ? 'Exportando…' : 'Exportar Excel'}
          </button>
        </div>
      </div>

      {hasQueried && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {totalsByClassification.map((group) => (
              <div key={group.classification} className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600 mb-1">{group.classification_label}</div>
                <div className="text-xl font-bold text-gray-800">
                  {formatMoney(group.total_amount)}
                </div>
                <div className="text-xs text-gray-500 mt-1">{group.count} documento{group.count === 1 ? '' : 's'}</div>
              </div>
            ))}
            {totalsByClassification.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-4 md:col-span-3 text-gray-500 text-sm">
                Sin documentos en {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </div>
            )}
          </div>

          {totalsByCurrency.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Totales por moneda</h2>
              <div className="flex flex-wrap gap-4">
                {totalsByCurrency.map((t) => (
                  <div key={t.currency} className="text-sm">
                    <span className="text-gray-500">{t.currency}:</span>{' '}
                    <span className="font-semibold">{formatMoney(t.total_amount, t.currency)}</span>
                    <span className="text-gray-400 ml-1">({t.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            {rows.length === 0 ? (
              <p className="p-8 text-center text-gray-500">No hay documentos para el mes seleccionado</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serie/Núm.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clasificación</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">IGV</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-teal-50 cursor-pointer"
                      onClick={() => openDetail(row.id)}
                      title="Ver detalle (insumos y referencias)"
                    >
                      <td className="px-4 py-3 text-sm text-gray-500">{row.row_number}</td>
                      <td className="px-4 py-3 text-sm font-mono text-teal-700 underline">{row.code}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.document_type_label}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.serie_numero}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{row.supplier_name}</div>
                        {row.supplier_document && (
                          <div className="text-xs text-gray-500">{row.supplier_document}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDateLocaleLima(row.issue_date)}
                      </td>
                      <td className="px-4 py-3">
                        {classificationBadge(row.classification, row.classification_label)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                        {formatMoney(row.base_imponible, row.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                        {formatMoney(row.igv_amount, row.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                        {formatMoney(row.total_amount, row.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {!hasQueried && !loading && (
        <div className="bg-white rounded-lg shadow-md p-10 text-center text-gray-500">
          Selecciona el mes y pulsa <span className="font-medium text-gray-700">Consultar</span> para ver el reporte.
        </div>
      )}

      {detail && (
        <DetailModal
          detail={detail}
          loading={detailLoading}
          onClose={closeDetail}
        />
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ComprasDiferenciadas;
